import { useState } from "react";
import { useLocation } from "wouter";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Decorative elements ──────────────────────────────────────────────────────

function FlowerBloom({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 80 80" className={className} fill="none">
      <circle cx="40" cy="40" r="8" fill="#f9a8d4" opacity="0.8" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <ellipse key={deg} cx={40 + 18 * Math.cos((deg * Math.PI) / 180)} cy={40 + 18 * Math.sin((deg * Math.PI) / 180)} rx="8" ry="12" fill="#fce7f3" opacity="0.75" transform={`rotate(${deg + 90} ${40 + 18 * Math.cos((deg * Math.PI) / 180)} ${40 + 18 * Math.sin((deg * Math.PI) / 180)})`} />
      ))}
      <circle cx="40" cy="40" r="7" fill="#f472b6" opacity="0.6" />
    </svg>
  );
}

function StarDoodle({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none">
      <path d="M20 4l3 10h10l-8 6 3 10-8-6-8 6 3-10-8-6h10z" fill="#fde68a" stroke="#f59e0b" strokeWidth="1" opacity="0.8" />
    </svg>
  );
}

function TapeStrip({ className, color = "#fde68a" }: { className?: string; color?: string }) {
  return <div className={cn("absolute w-16 h-5 opacity-60 rounded-sm", className)} style={{ background: color, transform: "rotate(-3deg)" }} />;
}

function PaperPin({ className }: { className?: string }) {
  return <div className={cn("absolute w-4 h-4 rounded-full shadow-md z-10", className)} style={{ background: "radial-gradient(circle at 35% 35%, #e11d48, #9f1239)", border: "2px solid #be123c" }} />;
}

function SketchArrow({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 60 30" className={className} fill="none">
      <path d="M4 18 Q20 8 48 15" stroke="#78716c" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 2" />
      <path d="M44 10 L50 16 L42 18" stroke="#78716c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WashiTape({ className, color = "#bbf7d0" }: { className?: string; color?: string }) {
  return <div className={cn("absolute h-7 opacity-50", className)} style={{ background: `repeating-linear-gradient(90deg, ${color} 0px, ${color} 12px, transparent 12px, transparent 16px)`, minWidth: "80px" }} />;
}

// ─── Illustration sticker ─────────────────────────────────────────────────────

function IllustrationSticker({ src, rotation = 0, size = 100, seed = 1, className }: {
  src?: string; rotation?: number; size?: number; seed?: number; className?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const actualSrc = (!src || failed) ? `https://picsum.photos/seed/s${seed}/200/200` : src;
  return (
    <div className={cn("relative inline-block", className)} style={{ transform: `rotate(${rotation}deg)`, width: size, height: size }}>
      {!loaded && <div className="absolute inset-0 rounded-lg bg-stone-100 animate-pulse opacity-50" />}
      <img src={actualSrc} alt="" onLoad={() => setLoaded(true)} onError={() => { if (!failed) { setFailed(true); setLoaded(false); } }}
        className={cn("w-full h-full object-cover rounded-lg transition-opacity duration-500", loaded ? "opacity-100" : "opacity-0")}
        style={{ filter: "saturate(0.85) contrast(1.05)", boxShadow: "2px 3px 12px rgba(0,0,0,0.14)" }} />
    </div>
  );
}

function PolaroidPhoto({ src, caption, rotation = 0, seed = 1, className }: {
  src?: string; caption?: string; rotation?: number; seed?: number; className?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const actualSrc = (!src || failed) ? `https://picsum.photos/seed/${seed + 42}/600/400` : src;
  return (
    <div className={cn("inline-block", className)} style={{ transform: `rotate(${rotation}deg)` }}>
      <div className="bg-white p-2 pb-6 shadow-[2px_4px_16px_rgba(0,0,0,0.18)] relative">
        <div className="w-full aspect-[4/3] overflow-hidden relative" style={{ background: "linear-gradient(135deg, #e8e0d4 0%, #d4cbc0 100%)" }}>
          {!loaded && <div className="absolute inset-0 flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-stone-300 border-t-transparent animate-spin opacity-40" /></div>}
          <img src={actualSrc} alt={caption ?? "Journal image"} onLoad={() => setLoaded(true)} onError={() => { if (!failed) { setFailed(true); setLoaded(false); } }}
            className={cn("w-full h-full object-cover transition-opacity duration-700", loaded ? "opacity-100" : "opacity-0")} />
        </div>
        {caption && <p className="text-center text-xs mt-2 text-stone-500 leading-tight px-1" style={{ fontFamily: "'Kalam', cursive" }}>{caption}</p>}
      </div>
    </div>
  );
}

function StickyNote({ children, color = "#fef9c3", rotation = 0, label, className }: {
  children: React.ReactNode; color?: string; rotation?: number; label?: string; className?: string;
}) {
  return (
    <div className={cn("relative p-5 shadow-md", className)} style={{ background: color, transform: `rotate(${rotation}deg)`, boxShadow: "2px 4px 14px rgba(0,0,0,0.10), inset 0 -2px 0 rgba(0,0,0,0.04)" }}>
      {label && <p className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-2">{label}</p>}
      {children}
    </div>
  );
}

function JournalStamp({ tag, date }: { tag: string; date: string }) {
  return (
    <div
      className="inline-flex flex-col items-center px-5 py-3 rounded-lg"
      style={{
        background: "rgba(16,185,129,0.07)",
        border: "1px solid rgba(16,185,129,0.12)",
        transform: "rotate(-2deg)",
        backdropFilter: "blur(8px)",
      }}
    >
      <span
        className="text-[11px] font-semibold uppercase tracking-[0.2em]"
        style={{ color: "#0a4f3a", fontFamily: "'DM Sans', sans-serif" }}
      >
        {tag}
      </span>
      <div className="w-5 h-px mx-auto my-1.5" style={{ background: "rgba(16,185,129,0.3)" }} />
      <span
        className="text-[10px] tracking-wide"
        style={{ color: "#0a4f3a", opacity: 0.5, fontFamily: "'DM Sans', sans-serif" }}
      >
        {date}
      </span>
    </div>
  );
}

function ActionItem({ done, onToggle, children }: { done: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onToggle} className="flex items-start gap-3.5 text-left group w-full py-3">
      <div className={cn("mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all", done ? "bg-stone-700 border-stone-700" : "border-stone-300 group-hover:border-stone-500")}>
        {done && <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none"><path d="M2.5 6l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
      </div>
      <span className={cn("text-sm leading-relaxed font-sans transition-all", done ? "line-through text-stone-400" : "text-stone-700")}>{children}</span>
    </button>
  );
}

const PAPER_STYLE: React.CSSProperties = {
  background: "#f7f7f5",
  backgroundImage: `radial-gradient(circle, rgba(168,165,160,0.05) 1.5px, transparent 1.5px)`,
  backgroundSize: "22px 22px",
};

const MOCK = {
  title: "The Permission I Never Needed",
  tag: "Growth",
  date: new Date().toISOString(),
  sourceTitle: "The Obstacle Is The Way — Ryan Holiday",
  mood: "quietly determined",
  pullQuote: "The door was never locked. You just kept knocking.",
  openingReflection: "You sat down today with something that had been quietly pulling at you — not loudly, not urgently, just persistently. The kind of content you return to because some part of you knows it's pointing at something real.",
  coreInsight: "Growth isn't waiting for you to feel ready. It's already happening in the moments when you're most uncomfortable, most uncertain, most tempted to look away.",
  whatSurfaced: "You've been circling this for a while. The hesitation isn't about capability — it's about permission. And you've been waiting for someone else to grant what only you can give yourself.",
  reflectionPrompt: "If you stopped waiting for certainty before acting — what would you do in the next 24 hours?",
  imageUrls: [
    "https://picsum.photos/seed/journal1/600/400",
    "https://picsum.photos/seed/journal2/600/400",
  ],
  // Sample illustration seeds — in a real journal these come from Openverse
  illustrationSeeds: [77, 88, 99, 111, 122, 133],
  actions: [
    { id: "a1", text: "Write down one thing you've been waiting for permission to do", completed: false },
    { id: "a2", text: "Take one concrete step toward it before Friday", completed: false },
    { id: "a3", text: "Tell someone you trust about this shift you want to make", completed: false },
  ],
};

const palette = { accent: "#10b981", stickyA: "#d1fae5", stickyB: "#fef9c3", washi: "#bbf7d0" };

export default function JournalPreview() {
  const [, setLocation] = useLocation();
  const [completedActions, setCompletedActions] = useState<Record<string, boolean>>({});
  const [noteOpen, setNoteOpen] = useState(false);

  return (
    <div style={PAPER_STYLE} className="journal-paper min-h-screen pb-20">
      {/* Nav */}
      <div className="sticky top-0 z-20 bg-[#f7f3ec]/80 backdrop-blur-sm border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setLocation("/unfoldings")} className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 transition-colors font-sans">
          <ArrowLeft className="w-4 h-4" /> Your Unfoldings
        </button>
        <span className="text-xs text-stone-400 font-sans italic">Preview entry</span>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-10 space-y-10">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="relative">
          <WashiTape color={palette.washi} className="-top-3 left-8 w-28" />
          {/* Illustration 0 */}
          <IllustrationSticker seed={MOCK.illustrationSeeds[0]} rotation={-8} size={88} className="absolute -right-2 top-0 z-10" />
          <div className="flex items-start justify-between gap-4 pt-3 pr-20">
            <div className="flex-1">
              <p className="text-xs text-stone-400 tracking-widest uppercase font-sans mb-1">
                {format(new Date(), "EEEE").toUpperCase()} · {format(new Date(), "MMMM d, yyyy")}
              </p>
              <h1 className="text-3xl md:text-4xl leading-tight text-stone-800 mt-1" style={{ fontFamily: "'Newsreader', serif", fontStyle: "italic" }}>
                {MOCK.title}
              </h1>
              <p className="mt-2 text-sm text-stone-400 font-sans italic">— {MOCK.mood}</p>
              <p className="mt-1 text-xs text-stone-400 font-sans">Unfolded from: <span className="italic">{MOCK.sourceTitle}</span></p>
            </div>
            <div className="shrink-0 mt-1">
              <JournalStamp tag={MOCK.tag} date={format(new Date(), "MMM d")} />
            </div>
          </div>
        </motion.div>

        {/* Image + Opening Reflection */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start relative">
          <IllustrationSticker seed={MOCK.illustrationSeeds[1]} rotation={10} size={78} className="absolute -right-3 -bottom-5 z-10 hidden md:block" />
          <div className="relative flex justify-center md:justify-start">
            <PaperPin className="-top-2 left-1/2 -translate-x-1/2" />
            <PolaroidPhoto src={MOCK.imageUrls[0]} caption={MOCK.mood} rotation={-2.5} seed={1} className="w-48 md:w-full max-w-[200px] md:max-w-none" />
          </div>
          <StickyNote color={palette.stickyA} rotation={1} label="Opening Reflection">
            <p className="leading-relaxed text-stone-800" style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "18px", lineHeight: "1.7" }}>
              {MOCK.openingReflection}
            </p>
          </StickyNote>
        </motion.div>

        {/* Pull Quote */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="relative py-6 flex items-center justify-center">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-16 rounded-full" style={{ background: palette.accent, opacity: 0.6 }} />
          <FlowerBloom className="absolute -right-2 -top-4 w-14 h-14 opacity-70" />
          <StarDoodle className="absolute right-12 -bottom-2 w-8 h-8 opacity-80" />
          <IllustrationSticker seed={MOCK.illustrationSeeds[2]} rotation={-6} size={72} className="absolute left-6 -bottom-6 z-10 hidden md:block opacity-85" />
          <p className="text-center px-10 md:px-8" style={{ fontFamily: "'Kalam', cursive", fontWeight: 700, fontSize: "26px", lineHeight: "1.45", color: palette.accent }}>
            "{MOCK.pullQuote}"
          </p>
        </motion.div>

        {/* Core Insight */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="relative">
          <TapeStrip className="-top-2.5 left-8" color={palette.washi} />
          <IllustrationSticker seed={MOCK.illustrationSeeds[3]} rotation={9} size={86} className="absolute -right-4 -top-5 z-10" />
          <div className="p-6 pt-8 pr-16 relative bg-white" style={{ boxShadow: "2px 4px 18px rgba(0,0,0,0.09)", borderLeft: `4px solid ${palette.accent}` }}>
            <p className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-3 font-sans">Core Insight</p>
            <p className="text-stone-800 leading-relaxed" style={{ fontFamily: "'Newsreader', serif", fontSize: "20px", fontStyle: "italic", lineHeight: "1.7" }}>
              {MOCK.coreInsight}
            </p>
          </div>
        </motion.div>

        {/* What Surfaced + Second Image */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start relative">
          <StickyNote color={palette.stickyB} rotation={-1} label="What Surfaced">
            <p className="leading-relaxed text-stone-800" style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "17px", lineHeight: "1.7" }}>
              {MOCK.whatSurfaced}
            </p>
          </StickyNote>
          <div className="relative flex justify-center md:justify-end">
            <SketchArrow className="absolute -left-6 top-8 w-12 h-8 opacity-60" />
            <PolaroidPhoto src={MOCK.imageUrls[1]} rotation={2} seed={2} className="w-44 md:w-full max-w-[180px] md:max-w-none" />
          </div>
          <IllustrationSticker seed={MOCK.illustrationSeeds[4]} rotation={-11} size={80} className="absolute -left-4 -bottom-4 z-10 hidden md:block" />
        </motion.div>

        {/* Reflection Prompt */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="relative overflow-visible p-6 rounded-sm" style={{ background: "#fff8f0", border: "1px dashed #d6cfc7", boxShadow: "1px 2px 8px rgba(0,0,0,0.06)" }}>
          <p className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-2 font-sans">Sit With This</p>
          <p className="text-stone-800 leading-relaxed" style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "19px", lineHeight: "1.65" }}>
            {MOCK.reflectionPrompt}
          </p>
          <StarDoodle className="absolute bottom-2 right-3 w-8 h-8 opacity-50" />
          <IllustrationSticker seed={MOCK.illustrationSeeds[5]} rotation={7} size={82} className="absolute -right-5 -top-5 z-10" />
        </motion.div>

        {/* Next Steps — clean body copy */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="relative">
          <TapeStrip className="-top-2.5 left-1/2 -translate-x-1/2" color={palette.washi} />
          <div className="bg-white pt-8 pb-4 px-6 relative" style={{ boxShadow: "2px 4px 14px rgba(0,0,0,0.07)" }}>
            <p className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-1 font-sans">Next steps</p>
            <div className="divide-y divide-stone-100">
              {MOCK.actions.map((action) => (
                <ActionItem key={action.id} done={completedActions[action.id] || false} onToggle={() => setCompletedActions((p) => ({ ...p, [action.id]: !p[action.id] }))}>
                  {action.text}
                </ActionItem>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Personal note */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <button onClick={() => setNoteOpen(!noteOpen)} className="flex items-center gap-2 text-sm text-stone-400 hover:text-stone-600 transition-colors font-sans mb-2">
            <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", noteOpen && "rotate-180")} />
            {noteOpen ? "Close note" : "Add a personal note…"}
          </button>
          {noteOpen && (
            <StickyNote color="#fef9c3">
              <textarea placeholder="Anything else on your mind…" className="w-full bg-transparent text-stone-700 placeholder:text-stone-400 focus:outline-none resize-none min-h-[80px]"
                style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "17px", lineHeight: "1.65" }} autoFocus />
            </StickyNote>
          )}
        </motion.div>

        {/* CTAs */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="flex flex-col gap-3 pt-4">
          <button onClick={() => setLocation("/unfoldings")} className="w-full flex items-center justify-center gap-2 py-4 rounded-full text-white font-sans font-semibold text-base hover:opacity-90 transition-all shadow-sm" style={{ background: palette.accent }}>
            <CheckCircle2 className="w-5 h-5" />
            Done — view my journal
          </button>
          <button onClick={() => setLocation("/library")} className="w-full text-center text-sm text-stone-400 hover:text-stone-600 transition-colors py-2 font-sans">
            Unfold another video
          </button>
        </motion.div>

      </div>
    </div>
  );
}
